import { useEffect, useId, useRef, useState } from "react";
import "./WebNavigation.css";

const PATHS = {
  dashboard: "M3 3h7v7H3z M14 3h7v7h-7z M3 14h7v7H3z M14 14h7v7h-7z",
  calc: "M6 2h12v20H6z M9 6h6 M9 11h1 M14 11h1 M9 15h1 M14 15h1 M9 19h1 M14 19h1",
  produtos: "M12 2l9 5v10l-9 5-9-5V7z M3 7l9 5 9-5 M12 12v10 M7 4.8l9 5",
  pedidos: "M8 4H5v18h14V4h-3 M8 2h8v4H8z M8 11h8 M8 16h6",
  site: "M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0 M3 12h18 M12 3c5 5 5 13 0 18 M12 3c-5 5-5 13 0 18",
  financeiro: "M12 2v20 M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6",
  caixa: "M2 3h3l3 12h11l3-9H6 M10 20h.01 M18 20h.01",
  fila: "M6 8V2h12v6 M6 17H3V8h18v9h-3 M6 14h12v8H6z M17 11h1",
  parceiros: "M3 9l1.5-5h15L21 9 M3 9v1a3 3 0 0 0 6 0 3 3 0 0 0 6 0 3 3 0 0 0 6 0V9 M5 13v8h14v-8 M10 21v-5h4v5",
};

function NavIcon({ id }) {
  return <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d={PATHS[id] || PATHS.dashboard} /></svg>;
}

export function WebNavigation({ tabs, activeTab, lateral, locked = false, onSelect, onReorder }) {
  const navRef = useRef(null);
  const draggedId = useRef(null);
  const [dragging, setDragging] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);
  const [announcement, setAnnouncement] = useState("");
  const instructionsId = useId();
  const clearDrag = () => {
    draggedId.current = null;
    setDragging(null);
    setDropTarget(null);
  };
  const moveTab = (from, to) => {
    if (locked || from < 0 || to < 0 || to >= tabs.length || from === to) return;
    onReorder(from, to);
    setAnnouncement(`${tabs[from].label} movida para a posição ${to + 1} de ${tabs.length}.`);
  };
  useEffect(() => { clearDrag(); }, [locked, lateral]);
  useEffect(() => {
    const nav = navRef.current;
    const active = nav?.querySelector('[aria-current="page"]');
    if (!active) return;
    // Rola apenas o menu: trocar a posição não desloca o conteúdo da página.
    if (lateral) nav.scrollTop = Math.max(0, active.offsetTop - nav.clientHeight / 2);
    else nav.scrollLeft = Math.max(0, active.offsetLeft - nav.clientWidth / 2);
  }, [activeTab, lateral]);

  return <aside className={`pc-web-nav pc-web-nav--${lateral ? "lateral" : "topo"}`}>
    {lateral && <div className="pc-web-nav-heading"><span>ESPAÇO DE TRABALHO</span><strong>Seu negócio</strong><small>{locked ? "Layout das abas travado." : "Arraste as abas para organizar."}</small></div>}
    <span id={instructionsId} className="pc-web-nav-sr-only">{locked ? "Layout travado. Destrave em Configurações, Aparência." : "Arraste para reordenar. Pelo teclado, use Alt e as setas na direção do menu."}</span>
    <nav ref={navRef} aria-label="Abas principais">
      {tabs.map((tab, index) => <div className={`pc-web-nav-item${dragging === tab.id ? " is-dragging" : ""}${dropTarget === tab.id && dragging !== tab.id ? " is-drop-target" : ""}`} key={tab.id}
        onDragOver={event => {
          if (locked || !draggedId.current) return;
          event.preventDefault();
          event.dataTransfer.dropEffect = "move";
          setDropTarget(tab.id);
          // Permite alcançar as abas fora da área visível durante o arrasto.
          const nav = navRef.current;
          const rect = nav.getBoundingClientRect();
          if (lateral) {
            if (event.clientY < rect.top + 30) nav.scrollTop -= 16;
            if (event.clientY > rect.bottom - 30) nav.scrollTop += 16;
          } else {
            if (event.clientX < rect.left + 30) nav.scrollLeft -= 16;
            if (event.clientX > rect.right - 30) nav.scrollLeft += 16;
          }
        }}
        onDrop={event => {
          if (locked || !draggedId.current) return;
          event.preventDefault();
          moveTab(tabs.findIndex(item => item.id === draggedId.current), index);
          clearDrag();
        }}>
        <button type="button" className="pc-web-nav-link" data-tour={`nav-${tab.id}`} aria-current={activeTab === tab.id ? "page" : undefined} aria-describedby={instructionsId} draggable={!locked}
          title={locked ? tab.label : `${tab.label} — arraste para reordenar`}
          onDragStart={event => {
            if (locked) { event.preventDefault(); return; }
            draggedId.current = tab.id;
            event.dataTransfer.effectAllowed = "move";
            event.dataTransfer.setData("text/plain", tab.id);
            setDragging(tab.id);
          }}
          onDragEnd={clearDrag}
          onKeyDown={event => {
            if (locked || !event.altKey) return;
            const direction = event.key === (lateral ? "ArrowUp" : "ArrowLeft") ? -1 : event.key === (lateral ? "ArrowDown" : "ArrowRight") ? 1 : 0;
            if (!direction) return;
            event.preventDefault();
            moveTab(index, index + direction);
          }}
          onClick={() => onSelect(tab.id)}>
          <NavIcon id={tab.id} /><span>{tab.label}</span>
        </button>
      </div>)}
    </nav>
    <span role="status" className="pc-web-nav-sr-only">{announcement}</span>
    {lateral && <div className="pc-web-nav-foot"><span className="pc-web-nav-status" />Print Control<span>Organize. Crie. Imprima.</span></div>}
  </aside>;
}

export function WebNavigationAppearance({ value, onChange, locked, onLockedChange }) {
  return <fieldset className="pc-nav-appearance">
    <legend>Posição das abas</legend>
    <p>Escolha como navegar pelo seu espaço de trabalho.</p>
    <div className="pc-nav-options">
      {[
        { id: "topo", label: "No topo", description: "Abas na horizontal, lado a lado." },
        { id: "lateral", label: "Na lateral", description: "Menu vertical, como no painel admin." },
      ].map(option => <label className="pc-nav-option" key={option.id}>
        <input type="radio" name="posicao-abas" value={option.id} checked={value === option.id} onChange={() => onChange(option.id)} />
        <span className={`pc-nav-preview pc-nav-preview--${option.id}`} aria-hidden="true"><span className="pc-nav-preview-header" /><span className="pc-nav-preview-menu"><i /><i /><i /></span><span className="pc-nav-preview-content"><i /><i /><i /></span></span>
        <span className="pc-nav-option-title">{option.label}<span className="pc-nav-option-check" aria-hidden="true">{value === option.id ? "✓" : ""}</span></span>
        <span className="pc-nav-option-description">{option.description}</span>
      </label>)}
    </div>
    <div className="pc-nav-lock">
      <div><strong>Travar layout das abas</strong><p>{locked ? "A ordem está protegida contra arrastos acidentais." : "Arraste e solte os botões do menu para mudar a ordem."}</p></div>
      <button type="button" role="switch" aria-label="Travar layout das abas" aria-checked={locked} onClick={() => onLockedChange(!locked)}><span /></button>
    </div>
    <small>Aplicado automaticamente e salvo neste navegador. Em telas pequenas, o menu se adapta ao espaço disponível.</small>
  </fieldset>;
}
